import React from 'react';
import Papa from 'papaparse';
import { isTotalFileSizeValid, isFileCountValid } from './validator';
import { UPLOAD_COUNT_EXCEEDED, UNSUPPORTED_FILE_FORMAT, MISSING_REQUIRED_COLUMN_HEADERS } from '../helpers/messageConstant';

/**
 * File Utils
 *
 * @author skit1169
 */

export function readFileContent(file) {
  const fileReader = new FileReader();
  fileReader.readAsText(file);
  return new Promise((resolve) => {
    fileReader.onload = (obj) => {
      resolve(obj.target.result);
    };
  });
}

export function readAsCSV(data, configs) {
  return Papa.parse(data, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: ((header) => header.trim()),
    ...configs
  });
}

export function getColumnHeaders(data) {
  return data && data.fields ? data.fields : [];
}

export function getMissingColumnHeaders(validHeaders, fileHeaders) {
  const missingColumnHeaders = [];
  validHeaders.forEach((value) => {
    if (fileHeaders.indexOf(value) === -1) {
      missingColumnHeaders.push(value);
    }
  });
  return missingColumnHeaders;
}

export async function generateMissingColumnHeadersError(files, columns) {
  const errors = [];
  await Promise.all(files.map(async (file) => {
    const data = readAsCSV(await readFileContent(file), {});
    const missingHeaders = getMissingColumnHeaders(columns, getColumnHeaders(data.meta));
    if (missingHeaders.length) {
      errors.push(`File: ${file.name} - Missing Headers: ${missingHeaders.join(', ')}`);
    }
  }));
  return errors;
}

export function getFormattedMessage(header, content) {
  return content.length ? (
  <React.Fragment>
    <span> {header} </span>
    {content.map((data, index) => (
      <li type={'none'} key={index} style={{ paddingTop: '15px' }}> {data} </li>
    ))}
  </React.Fragment>) : null;
}

export async function getBasicFileValidationErrors(files, fileExtension, columnHeaders, count, isTypeChecked, isMultiFile, rejectedFiles = []) {
  if (!isTotalFileSizeValid(count, files)) {
    return `Please ensure the total upload size is below ${count}MB`;
  }

  if (!isMultiFile && (((files?.length ?? 0) + (rejectedFiles?.length ?? 0)) > 1)) { // Total file count must not exceed 1
    return UPLOAD_COUNT_EXCEEDED;
  }

  if (isTypeChecked && files.length === 0) {
    return `${UNSUPPORTED_FILE_FORMAT} of .${fileExtension}`;
  }

  if (columnHeaders.length) {
    return getFormattedMessage(
      `${MISSING_REQUIRED_COLUMN_HEADERS} `,
      await generateMissingColumnHeadersError(files, columnHeaders)
    );
  }

  if (isFileCountValid(files, isMultiFile)) {
    return UPLOAD_COUNT_EXCEEDED;
  }
  return '';
}

export const convertToCSV = (data, columns) => {
  if (!data || (data && !data.length)) return '';

  const keys = columns ? Object.keys(columns) : Object.keys(data[0]);

  const escapeCSVValue = (value) => {
    if (value === null || value === undefined) return '';

    let stringValue = String(value);

    stringValue = stringValue.replace(/"/g, '""');

    if (/[",\n\r]/.test(stringValue)) {
      stringValue = `"${stringValue}"`;
    }

    return stringValue;
  };

  const header = columns ? `${Object.values(columns).map(escapeCSVValue).join(',')}\n` : `${keys.map(escapeCSVValue).join(',')}\n`;

  const rows = data
    .map((row) => keys.map((key) => escapeCSVValue(row[key])).join(','))
    .join('\n');

  return header + rows;
};

export const downloadCSV = (csv, fileName) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${fileName}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
